// The Nodes tab: a macro as a node graph (nodes.ts) on a canvas. Nodes are boxes with their inputs down the left and
// their output on the right; drag from an output to an input to wire them (a wire into a taken input replaces it),
// click a wire to take it out. Drag a node by its title, the background to pan, the wheel zooms; a click on the
// background (or right-click, or Add) opens a menu of every node kind, by category, and puts the one picked there. Saved, it is a macro like any other (the Macros tab runs it, a Macro step calls
// it); Run runs it as the Macros tab does (one Undo), each node marked as its steps land: its points, or why it
// stopped. The host (EditUI) owns the saved macros, the run and the inputs (its Macros tab's, so the two look alike).
// A Macro or For each node shows its macro's data variables as ports (in on the left, out on the right), kept as that
// macro is now each time the graph draws, and its other variables as inputs; double-click its title to open the macro.
// A Sub Graph node likewise shows its graph's Graph inputs as inputs, its Result and Graph outputs as outputs.
import type { Field, Macro, Params, RunStep, StepKind } from './macros.ts';
import { dataVars, kindOf, shown, varOf, varsOf } from './macros.ts';
import type { Out } from './macroeval.ts';
import { type GNode, type Graph, type GraphIo, type IoPort, type NodeDef, type Wire, MAX_INS, MAX_NODES, compile, evalPorts, graphPorts, hasIo, insOf, newNode, nextId, nodeDef, nodeKinds, nodeName, outsOf, parseGraph, sinkOrder, typeName } from './nodes.ts';

/** What a run tells the canvas: a step landed (out), or the run stopped there (why). */
export type Tap = (r: RunStep, out: Out | null, why?: string) => void;

export interface NodeHost {
  macros(): Macro[];
  save(m: Macro): Promise<void>;
  drop(name: string): Promise<void>;
  /** Run m (one Undo): whether it ran to its end, and what the run said last. */
  run(m: Macro, tap: Tap): Promise<{ ok: boolean; say: string }>;
  /** An input for a node's parameter, as the Macros tab has them (redraw(true): the node's fields may change). */
  field(f: Field, get: () => unknown, put: (v: number | string | boolean) => void, o: { vars?: boolean; self?: string; redraw: (all: boolean) => void; say: (msg: string, warn?: boolean) => void }): HTMLElement;
  /** A step kind's parameters as the toolbar has them (a new node starts from them). */
  toolbarParams(t: StepKind): Params;
  /** The other tab is to show this macro: the Macros tab, for a list macro picked here. */
  openList(m: Macro): void;
}

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const SVG = 'http://www.w3.org/2000/svg';
const svg = (tag: string, attrs: Record<string, string> = {}) => {
  const e = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
};
const CATS = ['Select', 'Combine', 'Change', 'Data', 'Macro', 'Graph', 'Output', 'Photo'];
const blank = (): Macro => ({ name: '', steps: [], graph: { nodes: [], wires: [] } });

export class NodeUI {
  private m: Macro = blank();
  private view = { x: 40, y: 30, k: 1 };
  private picked: string | null = null;
  /** After a run: per node, its points (a selection's), what it said, whether it stopped the run. */
  private marks = new Map<string, { n?: number; say?: string; bad?: boolean }>();
  private running: string | null = null;
  private els = new Map<string, HTMLElement>();
  private readonly canvas: HTMLElement;
  private readonly world: HTMLElement;
  private readonly wires: SVGSVGElement;
  private readonly info: HTMLElement;
  private readonly bar: { sel: HTMLSelectElement; name: HTMLInputElement; save: HTMLButtonElement; del: HTMLButtonElement; run: HTMLButtonElement };
  /** The pointerdown that closed the menu (clicking away): it doesn't open it again. */
  private shut: Event | null = null;
  private readonly vars: HTMLElement;

  constructor(private root: HTMLElement, private host: NodeHost) {
    root.textContent = '';
    const row = el('div', { class: 'row' });
    const sel = el('select', { title: 'a saved node graph to edit or run' }) as HTMLSelectElement;
    const name = el('input', { type: 'text', placeholder: 'name', maxlength: '60', style: 'width:9em' }) as HTMLInputElement;
    const save = el('button', { class: 'small' }, 'Save') as HTMLButtonElement;
    const del = el('button', { class: 'small', title: 'delete the saved macro of this name' }, 'Delete') as HTMLButtonElement;
    const run = el('button', { class: 'small', title: 'run the graph on the scans being edited (one Undo)' }, 'Run') as HTMLButtonElement;
    const addGo = el('button', { class: 'small', title: 'add a node (or click the canvas where it goes)' }, 'Add…') as HTMLButtonElement;
    const fit = el('button', { class: 'small', title: 'show every node' }, 'Fit') as HTMLButtonElement;
    row.append(sel, name, save, del, run, addGo, fit);
    this.bar = { sel, name, save, del, run };
    this.vars = el('div', { class: 'nvars' });
    this.canvas = el('div', { class: 'ncanvas', tabindex: '0' });
    this.world = el('div', { class: 'nworld' });
    this.wires = svg('svg', { class: 'nwires' }) as SVGSVGElement;
    this.world.append(this.wires);
    this.info = el('div', { class: 'mono ninfo' });   // the status: over the canvas at its top, taking no room of its own
    this.canvas.append(this.world, this.info);
    root.append(row, this.vars, this.canvas);

    sel.onchange = () => {
      this.open(this.host.macros().find(x => x.name === sel.value && x.graph) ?? blank());
    };
    name.oninput = () => { this.m.name = name.value; this.drawBar(); };
    save.onclick = () => void this.save();
    del.onclick = () => void this.drop();
    run.onclick = () => void this.run();
    addGo.onclick = () => {
      const r = this.canvas.getBoundingClientRect(), b = addGo.getBoundingClientRect();
      this.menu(b.left, b.bottom + 2, this.toWorld(r.left + r.width / 3, r.top + r.height / 3));
    };
    fit.onclick = () => { this.fit(); this.place(); };
    this.wireCanvas();
    this.open(blank());
  }

  private get g(): Graph { return this.m.graph!; }

  /** Edit macro m (a node graph; a new one: blank). */
  open(m: Macro) {
    this.m = structuredClone(m);
    this.m.graph ??= { nodes: [], wires: [] };
    this.picked = null;
    this.marks.clear();
    this.say('');
    this.fit();
    this.draw();
  }

  /** The saved macros changed (elsewhere too): the picker, and whether this one is saved. */
  refresh() { this.drawBar(); }

  private saved() { const n = this.m.name.trim(); return this.host.macros().find(x => x.name === n); }

  private say(msg: string, warn = false) { this.info.textContent = msg; this.info.classList.toggle('warn', warn); }

  // ── drawing ──
  private draw() {
    this.syncPorts();
    this.drawBar();
    this.drawVars();
    for (const e of this.els.values()) e.remove();
    this.els.clear();
    const order = new Map(sinkOrder(this.g).map((n, i) => [n.id, i + 1]));
    for (const n of this.g.nodes) {
      const e = this.nodeEl(n, order.get(n.id));
      this.els.set(n.id, e);
      this.world.append(e);
    }
    this.place();
  }

  private drawBar() {
    const { sel, name, save, del, run } = this.bar, saved = this.saved(), nm = this.m.name.trim();
    sel.textContent = '';
    sel.append(el('option', { value: '' }, '(new node graph)'));
    for (const m of this.host.macros()) if (m.graph) sel.append(el('option', { value: m.name }, m.name));
    sel.value = saved?.graph ? nm : '';
    if (name.value !== this.m.name) name.value = this.m.name;
    const dirty = !saved || JSON.stringify(saved.graph) !== JSON.stringify(this.g) || JSON.stringify(saved.vars ?? {}) !== JSON.stringify(this.m.vars ?? {});
    save.classList.toggle('on', dirty && !!nm && this.g.nodes.length > 0);
    save.title = saved && !saved.graph ? `${nm} is a list macro: saving replaces it with this graph` : saved ? (dirty ? `save this graph over the saved ${nm}` : 'saved') : 'save this graph under this name (for this scene)';
    del.disabled = !saved?.graph;
    run.disabled = !this.g.nodes.length;
  }

  /** The graph's variables (<name> in a node's inputs): their values when it runs. */
  private drawVars() {
    const m = this.m, uses = varsOf(m, this.host.macros()), vars: Params = {};
    for (const [n, fs] of uses) { const v = m.vars?.[n] ?? fs[0]?.dflt; if (v !== undefined) vars[n] = v; }
    if (Object.keys(vars).length) m.vars = vars; else delete m.vars;
    this.vars.textContent = '';
    this.vars.hidden = !uses.size;
    for (const [n, fs] of uses) {
      const f = fs[0] ?? { k: 'num', key: n, label: '', dflt: 0 } as Field, row = el('span', { class: 'nvar' });
      row.append(el('b', {}, `<${n}>`), this.host.field({ ...f, label: '' } as Field, () => vars[n], v => { (m.vars ??= {})[n] = v; }, { redraw: () => this.drawBar(), say: (s, w) => this.say(s, w) }));
      this.vars.append(row);
    }
  }

  private nodeEl(n: GNode, order?: number): HTMLElement {
    const d = nodeDef(n.t)!, mark = this.marks.get(n.id), ins = insOf(n);
    const e = el('div', { class: `nnode cat-${d.cat.toLowerCase()}${n.id === this.picked ? ' picked' : ''}${mark?.bad ? ' bad' : ''}${mark && !mark.bad ? ' ran' : ''}${n.id === this.running ? ' run' : ''}`, 'data-id': n.id });
    const head = el('div', { class: 'nhead', title: `${d.title}${mark?.say ? `\n\n${mark.say}` : ''}` });
    head.append(el('span', { class: 'nlabel' }, d.label));
    if (order) head.append(el('span', { class: 'norder', title: 'outputs run in this order: top to bottom on the canvas (then left to right), Result last' }, `#${order}`));
    if (mark?.n !== undefined) head.append(el('span', { class: 'ncount', title: 'points it selected in the last run' }, mark.n.toLocaleString()));
    const x = el('button', { class: 'small nx', title: 'remove this node' }, '✕');
    x.onclick = ev => { ev.stopPropagation(); this.remove(n.id); };
    head.append(x);
    const outs = outsOf(n), sub = d.role === 'sub';
    if (outs[0] && !sub) head.append(el('span', { class: `nport out t-${outs[0].type}`, 'data-out': `${n.id}:0`, title: `output: a ${typeName(outs[0].type)}; drag to an input` }));
    if (d.role === 'macro' || sub) { head.title += `\n\ndouble-click to open its ${sub ? 'graph' : 'macro'}`; head.ondblclick = () => this.openBody(n); }
    e.append(head);
    const body = el('div', { class: 'nbody' });
    const wired = this.wiredInto(n.id);
    ins.forEach((p, i) => {
      const r = el('div', { class: 'nin' });
      r.append(el('span', { class: `nport in t-${p.type}${wired[i] ? ' on' : ''}`, 'data-in': `${n.id}:${i}`, title: `${p.name}: a ${typeName(p.type)}${p.req ? '' : ' (may be left unwired)'}${wired[i] ? `; from ${nodeName(this.node(wired[i]!)!)}` : ''}` }), el('span', { class: 'npn' + (p.req ? '' : ' opt') }, p.name));
      body.append(r);
    });
    (sub ? outs : outs.slice(1)).forEach((p, k0) => {   // a Macro or For each node's data ports: what its macro left in them; a Sub Graph node's every output
      const k = sub ? k0 : k0 + 1, r = el('div', { class: 'nout' });
      const title = !sub ? `${p.name}: the data group as the macro left it (a copy of what is wired into ${p.name})`
        : k === 0 && n.io?.outs[0]?.result ? 'result: the selection its Result was given' : `${p.name}: the ${typeName(p.type)} its Graph output gives out`;
      r.append(el('span', { class: 'npn' }, p.name), el('span', { class: `nport out t-${p.type}`, 'data-out': `${n.id}:${k}`, title: `${title}; drag to an input` }));
      body.append(r);
    });
    const fs = el('div', { class: 'nfields' });
    const redraw = (all: boolean) => { if (all) this.draw(); else this.drawBar(); };
    for (const f of d.fields) if (this.fieldShown(n, d, f, wired)) fs.append(this.host.field(f, () => n.p[f.key], v => { n.p[f.key] = v; }, { vars: !(f.k === 'macro' && f.graphs), self: this.m.name.trim(), redraw, say: (s, w) => this.say(s, w) }));
    if (d.role === 'macro' || sub) this.argFields(n, fs, wired);
    if (fs.childNodes.length) body.append(fs);
    if (mark?.bad && mark.say) body.append(el('div', { class: 'nwhy' }, mark.say));
    e.append(body);
    head.onpointerdown = ev => this.dragNode(ev, n);
    e.onpointerdown = ev => {
      // the canvas takes the keys (Del removes the node): never the editor's, whose Delete deletes the selected points
      if (!(ev.target as HTMLElement).closest('input, select, textarea, button')) this.canvas.focus({ preventScroll: true });
      if (this.picked !== n.id) { this.picked = n.id; for (const [id, x2] of this.els) x2.classList.toggle('picked', id === n.id); }
    };
    return e;
  }

  /** A node's field shows: for its parameters as they are, and not where a wire gives it (Data math's B, Data band's group). */
  private fieldShown(n: GNode, d: NodeDef, f: Field, wired: (string | null)[]) {
    if (!shown(f, n.p)) return false;
    if (n.t === 'dataOp' && wired[1] && (f.key === 'src' || (f.on && f.on[0] === 'src'))) return false;
    if (n.t === 'dataBand' && wired[0] && f.key === 'name') return false;
    return d.fields.includes(f);
  }

  /** A Macro, For each or Sub Graph node's macro, saved (none: not picked, a variable, or not saved). */
  private bodyOf(n: GNode): Macro | undefined {
    const name = String(n.t === 'blobs' ? n.p.body : n.p.name);
    return name && !varOf(name) ? this.host.macros().find(x => x.name === name) : undefined;
  }

  /** A Macro, For each or Sub Graph node's values for its macro's variables, but For each's own, the data ports wired (a Sub Graph's: all its ports). */
  private argFields(n: GNode, fs: HTMLElement, wired: (string | null)[]) {
    const sub = this.bodyOf(n);
    if (!sub) return;
    const uses = varsOf(sub, this.host.macros()), own = n.t === 'blobs' ? String(n.p.var) : '';
    if (n.args) { for (const v of Object.keys(n.args)) if (!uses.has(v)) delete n.args[v]; if (!Object.keys(n.args).length) delete n.args; }
    for (const [v, fl] of uses) {
      const k = n.ports?.indexOf(v) ?? -1;
      if (v === own || (k >= 0 && (wired[k + 1] || this.g.wires.some(w => w.from === n.id && w.out === k + 1)))) continue;
      if (n.io && [...n.io.ins, ...n.io.outs].some(p => !p.result && p.name === v)) continue;
      const f = fl[0] ?? { k: 'num', key: v, label: v, dflt: 0 } as Field, set = !!n.args && Object.hasOwn(n.args, v);
      const a = el('span', { class: 'emarg' + (set ? '' : ' dflt'), title: set ? `${v}: passed to ${sub.name}` : `${v}: ${sub.name}'s default` });
      a.append(this.host.field({ ...f, key: v, label: v } as Field, () => n.args?.[v] ?? sub.vars?.[v] ?? f.dflt, x => { (n.args ??= {})[v] = x; }, { vars: true, redraw: () => this.draw(), say: (s, w) => this.say(s, w) }));
      if (set) {
        const b = el('button', { class: 'small', title: `back to ${sub.name}'s default` }, '↺');
        b.onclick = () => { delete n.args![v]; if (!Object.keys(n.args!).length) delete n.args; this.draw(); };
        a.append(b);
      }
      fs.append(a);
    }
  }

  /** Each Macro or For each node's data ports as its macro has them now, a Sub Graph node's its graph's; wires to ports gone (or of another type) go. */
  private syncPorts() {
    for (const n of this.g.nodes) {
      if (n.t === 'macro') this.toSubGraph(n);
      if (n.t === 'subgraph') { this.syncIo(n); continue; }
      if (n.t === 'dataEval') { this.syncEval(n); continue; }
      if (nodeDef(n.t)?.role !== 'macro') continue;
      const sub = this.bodyOf(n);
      if (!sub) continue;
      const own = n.t === 'blobs' ? String(n.p.var) : '';
      const ps = dataVars(sub, this.host.macros()).filter(v => v !== own).slice(0, MAX_INS - 1), was = n.ports ?? [];
      if (ps.join() === was.join()) continue;
      const at = (k: number) => (k > 0 ? ps.indexOf(was[k - 1]) + 1 : 0);   // a port's place now (0: gone, but the selection's)
      this.g.wires = this.g.wires.flatMap(w => {
        if (w.to === n.id && w.port > 0) { const p = at(w.port); return p ? [{ ...w, port: p }] : []; }
        if (w.from === n.id && (w.out ?? 0) > 0) { const o = at(w.out!); return o ? [{ ...w, out: o }] : []; }
        return [w];
      });
      if (ps.length) n.ports = ps; else delete n.ports;
    }
    // a Graph input's type changed, a port gone: wires that no longer fit go
    const before = this.g.wires.length;
    this.g.wires = this.g.wires.filter(w => {
      const a = this.node(w.from), b = this.node(w.to), o = a && outsOf(a)[w.out ?? 0], i = b && insOf(b)[w.port];
      return !!o && !!i && o.type === i.type;
    });
    if (this.g.wires.length < before) this.say(`${before - this.g.wires.length} wire${before - this.g.wires.length === 1 ? '' : 's'} taken out: what they joined changed`, true);
  }

  /**
   * A Macro node running a graph with Graph inputs or outputs becomes a Sub Graph node (its ports the graph's inputs
   * and outputs, not every data variable both ways); its wires go with it: its data ports' by name, its output to Result.
   */
  private toSubGraph(n: GNode) {
    const sub = this.bodyOf(n);
    if (!sub?.graph || !hasIo(sub.graph)) return;
    let io: GraphIo;
    try { io = graphPorts(sub.graph); } catch { return; }   // syncIo says why, once it is one
    const ps = n.ports ?? [], data = (side: IoPort[], k: number) => (k > 0 ? side.findIndex(p => p.type === 'data' && p.name === ps[k - 1]) : -1);
    this.g.wires = this.g.wires.flatMap(w => {
      if (w.to === n.id) { const p = data(io.ins, w.port); return p >= 0 ? [{ ...w, port: p }] : []; }
      if (w.from === n.id) {
        const k = w.out ?? 0, o = k ? data(io.outs, k) : io.outs.findIndex(p => p.result), { out: _, ...r } = w;
        return o >= 0 ? [o ? { ...r, out: o } : r] : [];
      }
      return [w];
    });
    n.t = 'subgraph';
    n.p = { name: n.p.name };
    delete n.ports;
    if (io.ins.length || io.outs.length) n.io = io;
    this.say(`${nodeName(n)}: ${sub.name} has Graph inputs or outputs, so this runs it as a Sub Graph node (save to keep it)`);
  }

  /** A Data eval node's inputs as its expression reads now (it can't be read: as they were), wires following them by name. */
  private syncEval(n: GNode) {
    const ps = evalPorts(n), was = n.ports ?? [];
    if (!ps || ps.join('\n') === was.join('\n')) return;
    this.g.wires = this.g.wires.flatMap(w => {
      if (w.to !== n.id || w.port === 0) return [w];
      const p = ps.indexOf(was[w.port - 1]) + 1;
      return p ? [{ ...w, port: p }] : [];
    });
    if (ps.length) n.ports = ps; else delete n.ports;
  }

  /** A Sub Graph node's ports as its graph has them now (none picked: none), its wires following them by name. */
  private syncIo(n: GNode) {
    const sub = this.bodyOf(n);
    if (n.p.name && !sub) return;   // not saved (here): kept as last seen
    let io: GraphIo = { ins: [], outs: [] };
    if (sub?.graph) {
      try { io = graphPorts(sub.graph); } catch (e) { this.say(`${nodeName(n)}: ${n.p.name}: ${(e as Error).message}`, true); return; }
    }
    const was = n.io ?? { ins: [], outs: [] };
    if (JSON.stringify(io) === JSON.stringify(was)) return;
    const at = (from: IoPort[], to: IoPort[], k: number) => { const p = from[k]; return p ? to.findIndex(q => q.name === p.name && q.type === p.type && !!q.result === !!p.result) : -1; };
    this.g.wires = this.g.wires.flatMap(w => {
      if (w.to === n.id) { const p = at(was.ins, io.ins, w.port); return p >= 0 ? [{ ...w, port: p }] : []; }
      if (w.from === n.id) { const o = at(was.outs, io.outs, w.out ?? 0), { out: _, ...r } = w; return o >= 0 ? [o ? { ...r, out: o } : r] : []; }
      return [w];
    });
    if (io.ins.length || io.outs.length) n.io = io; else delete n.io;
  }

  /** Double-click a Macro, For each or Sub Graph node: open its macro (a graph here, a list in the Macros tab). */
  private openBody(n: GNode) {
    const sub = this.bodyOf(n);
    if (!sub) { this.say('pick a saved macro first', true); return; }
    const saved = this.saved();
    if ((!saved || JSON.stringify(saved.graph) !== JSON.stringify(this.g)) && this.g.nodes.length && !confirm(`Leave ${this.m.name.trim() || 'this graph'} without saving it?`)) return;
    if (sub.graph) this.open(sub); else this.host.openList(sub);
  }

  private node(id: string) { return this.g.nodes.find(n => n.id === id); }
  private wiredInto(id: string): (string | null)[] {
    const n = this.node(id)!, out: (string | null)[] = insOf(n).map(() => null);
    for (const w of this.g.wires) if (w.to === id && w.port < out.length) out[w.port] = w.from;
    return out;
  }

  /** Nodes where they are, the view panned and zoomed, the wires between their ports. */
  private place() {
    const { x, y, k } = this.view;
    this.world.style.transform = `translate(${x}px, ${y}px) scale(${k})`;
    this.canvas.style.setProperty('--gx', `${x}px`); this.canvas.style.setProperty('--gy', `${y}px`); this.canvas.style.setProperty('--gk', `${k}`);   // the grid pans and zooms with it
    for (const n of this.g.nodes) { const e = this.els.get(n.id); if (e) { e.style.left = `${n.x}px`; e.style.top = `${n.y}px`; } }
    this.drawWires();
  }

  private portAt(sel: string): [number, number] | null {
    const p = this.world.querySelector<HTMLElement>(sel);
    if (!p) return null;
    const r = p.getBoundingClientRect(), w = this.world.getBoundingClientRect(), k = this.view.k;
    return [(r.left + r.width / 2 - w.left) / k, (r.top + r.height / 2 - w.top) / k];
  }

  private curve(a: [number, number], b: [number, number]) {
    const dx = Math.max(30, Math.abs(b[0] - a[0]) / 2);
    return `M${a[0]},${a[1]} C${a[0] + dx},${a[1]} ${b[0] - dx},${b[1]} ${b[0]},${b[1]}`;
  }

  private drawWires() {
    this.wires.textContent = '';
    for (const w of this.g.wires) {
      const a = this.portAt(`[data-out="${w.from}:${w.out ?? 0}"]`), b = this.portAt(`[data-in="${w.to}:${w.port}"]`);
      if (!a || !b) continue;
      const t = outsOf(this.node(w.from)!)[w.out ?? 0]?.type ?? 'sel';
      const path = svg('path', { d: this.curve(a, b), class: `nwire t-${t}` });
      const hit = svg('path', { d: this.curve(a, b), class: 'nwirehit' });
      const title = svg('title');
      title.textContent = `${nodeName(this.node(w.from)!)} → ${nodeName(this.node(w.to)!)} (click to take it out)`;
      hit.append(title);
      hit.addEventListener('click', () => { this.g.wires = this.g.wires.filter(x => x !== w); this.changed(); });
      this.wires.append(path, hit);
    }
  }

  /** The graph changed: drop what the last run said, redraw. */
  private changed() { this.marks.clear(); this.draw(); }

  // ── editing ──
  private toWorld(cx: number, cy: number): [number, number] {
    const r = this.canvas.getBoundingClientRect(), { x, y, k } = this.view;
    return [(cx - r.left - x) / k, (cy - r.top - y) / k];
  }

  private addAt(t: string, [x, y]: [number, number]) {
    if (this.g.nodes.length >= MAX_NODES) { this.say(`${MAX_NODES} nodes at most`, true); return; }
    const from = kindOf(t) ? this.host.toolbarParams(t as StepKind) : {};
    const n = newNode(t, nextId(this.g), Math.round(x), Math.round(y), from);
    this.g.nodes.push(n);
    this.picked = n.id;
    this.changed();
  }

  private remove(id: string) {
    this.g.nodes = this.g.nodes.filter(n => n.id !== id);
    this.g.wires = this.g.wires.filter(w => w.from !== id && w.to !== id);
    if (this.picked === id) this.picked = null;
    this.changed();
  }

  /** Wire output k of from into input port of to (replacing what was wired there); a variadic node gets a spare input. */
  private connect(from: string, k: number, to: string, port: number) {
    const a = this.node(from)!, b = this.node(to)!, out = outsOf(a)[k]?.type, ins = insOf(b);
    if (from === to) return;
    if (!out || !ins[port]) return;
    if (ins[port].type !== out) { this.say(`${nodeName(a)} gives a ${typeName(out)}; ${ins[port].name} of ${nodeName(b)} takes a ${typeName(ins[port].type)}`, true); return; }
    const w: Wire = { from, to, port, ...(k ? { out: k } : {}) };
    const wires = this.g.wires.filter(x => !(x.to === to && x.port === port)).concat(w);
    try { compileLoops({ ...this.g, wires }, to); } catch (e) { this.say((e as Error).message, true); return; }
    this.g.wires = wires;
    if (nodeDef(b.t)!.variadic) {
      const wired = this.wiredInto(to);
      if (wired.every(Boolean) && wired.length < MAX_INS) b.n = wired.length + 1;
    }
    this.say('');
    this.changed();
  }

  private dragNode(ev: PointerEvent, n: GNode) {
    if (ev.button !== 0 || (ev.target as HTMLElement).closest('button, .nport')) return;
    ev.preventDefault();
    const x0 = ev.clientX, y0 = ev.clientY, nx = n.x, ny = n.y, k = this.view.k, e = this.els.get(n.id)!;
    const move = (m: PointerEvent) => { n.x = Math.round(nx + (m.clientX - x0) / k); n.y = Math.round(ny + (m.clientY - y0) / k); e.style.left = `${n.x}px`; e.style.top = `${n.y}px`; this.drawWires(); };
    const up = () => { removeEventListener('pointermove', move); removeEventListener('pointerup', up); if (n.x !== nx || n.y !== ny) this.draw(); };   // sinks may run in another order now
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  }

  private wireCanvas() {
    const c = this.canvas;
    c.addEventListener('keydown', e => {
      if (!e.ctrlKey && !e.metaKey) e.stopPropagation();   // the editor's keys (Delete deletes points) are not for here; Ctrl+Z still undoes
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.picked && !(e.target as HTMLElement).closest('input, select')) { e.preventDefault(); this.remove(this.picked); }
    });
    c.addEventListener('wheel', e => {
      e.preventDefault();
      const [wx, wy] = this.toWorld(e.clientX, e.clientY), k = Math.min(2, Math.max(0.25, this.view.k * Math.exp(-e.deltaY * 0.0015)));
      const r = c.getBoundingClientRect();
      this.view = { k, x: e.clientX - r.left - wx * k, y: e.clientY - r.top - wy * k };
      this.place();
    }, { passive: false });
    c.addEventListener('contextmenu', e => {
      if ((e.target as HTMLElement).closest('.nnode')) return;
      e.preventDefault();
      this.menu(e.clientX, e.clientY);
    });
    c.addEventListener('pointerdown', e => {
      const t = e.target as HTMLElement;
      const out = t.closest<HTMLElement>('[data-out]'), inp = t.closest<HTMLElement>('[data-in]');
      if (out || inp) { e.preventDefault(); this.dragWire(e, out, inp); return; }
      if (t.closest('.nnode, .nmenu') || e.button !== 0) return;
      c.focus();
      this.picked = null;
      for (const x of this.els.values()) x.classList.remove('picked');
      // a drag pans; a click (not on a wire, nor the one that closed the menu) opens the menu there
      const x0 = e.clientX, y0 = e.clientY, v0 = { ...this.view }, opens = this.shut !== e && !t.closest('.nwirehit');
      let moved = false;
      const move = (m: PointerEvent) => {
        moved ||= Math.hypot(m.clientX - x0, m.clientY - y0) > 4;
        this.view = { ...v0, x: v0.x + m.clientX - x0, y: v0.y + m.clientY - y0 }; this.place();
      };
      const up = () => { removeEventListener('pointermove', move); removeEventListener('pointerup', up); if (!moved && opens) this.menu(x0, y0); };
      addEventListener('pointermove', move);
      addEventListener('pointerup', up);
    });
  }

  /** Drag a wire: from an output, or out of a wired input (taking that wire up, from its output), dropped on a port. */
  private dragWire(e: PointerEvent, out: HTMLElement | null, inp: HTMLElement | null) {
    let [from, k] = out ? [out.dataset.out!.split(':')[0], +out.dataset.out!.split(':')[1]] : [null, 0];
    if (!from && inp) {   // a wired input: pick its wire up
      const [id, p] = inp.dataset.in!.split(':'), w = this.g.wires.find(x => x.to === id && x.port === +p);
      if (!w) { this.say('drag from an output (the dot at the right of a node\'s title) to here'); return; }
      this.g.wires = this.g.wires.filter(x => x !== w);
      [from, k] = [w.from, w.out ?? 0];
      this.drawWires();
    }
    const a = this.portAt(`[data-out="${from}:${k}"]`);
    if (!from || !a) return;
    const t = outsOf(this.node(from)!)[k]?.type ?? 'sel';
    const path = svg('path', { class: `nwire t-${t} live` });
    this.wires.append(path);
    const move = (m: PointerEvent) => path.setAttribute('d', this.curve(a, this.toWorld(m.clientX, m.clientY)));
    move(e);
    const up = (m: PointerEvent) => {
      removeEventListener('pointermove', move); removeEventListener('pointerup', up);
      path.remove();
      const hit = (document.elementFromPoint(m.clientX, m.clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-in]');
      if (hit) { const [id, p] = hit.dataset.in!.split(':'); this.connect(from!, k, id, +p); } else this.changed();
    };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  }

  /**
   * A menu of every node kind, in columns by category so most show at once, centred on the pointer at (cx, cy); picking
   * one puts it there. From the Add button (`at` given: where the node goes) it hangs from (cx, cy) instead. Typing filters it, Enter adds the first left, Escape closes it.
   */
  private menu(cx: number, cy: number, under?: [number, number]) {
    const at = under ?? this.toWorld(cx, cy);
    document.querySelector('.nmenu')?.remove();
    const m = el('div', { class: 'nmenu' });   // over the page, not the canvas: the tab may be short
    const find = el('input', { type: 'search', placeholder: 'find a node…', class: 'nmfind' }) as HTMLInputElement;
    const cols = el('div', { class: 'nmcols' });
    m.append(find, cols);
    const close = () => { m.remove(); removeEventListener('pointerdown', away, true); };
    const away = (e: Event) => { if (!m.contains(e.target as Node)) { this.shut = e; close(); } };
    const items: { b: HTMLButtonElement; text: string; grp: HTMLElement }[] = [];
    for (const c of CATS) {
      const kinds = Object.entries(nodeKinds()).filter(([, d]) => d.cat === c);
      if (!kinds.length) continue;
      const grp = el('div', { class: `nmgrp cat-${c.toLowerCase()}` });
      grp.append(el('div', { class: 'nmcat' }, c));
      for (const [t, d] of kinds) {
        const b = el('button', { title: d.title }, d.label) as HTMLButtonElement;
        b.onclick = () => { close(); this.addAt(t, at); };
        grp.append(b);
        items.push({ b, text: `${d.label} ${c}`.toLowerCase(), grp });
      }
      cols.append(grp);
    }
    const shown = () => items.filter(x => !x.b.hidden);
    find.oninput = () => {
      const q = find.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
      for (const x of items) x.b.hidden = !q.every(w => x.text.includes(w));
      for (const g of cols.children as HTMLCollectionOf<HTMLElement>) g.hidden = !items.some(x => x.grp === g && !x.b.hidden);
    };
    m.addEventListener('keydown', e => {
      e.stopPropagation();
      if (e.key === 'Escape') close();
      else if (e.key === 'Enter' && e.target === find) { e.preventDefault(); shown()[0]?.b.click(); }
    });
    document.body.append(m);
    // round the pointer (below the Add button), kept on the page (it scrolls when the page is smaller than it)
    const w = m.offsetWidth, h = m.offsetHeight, x = under ? cx : cx - w / 2, y = under ? cy : cy - h / 2;
    m.style.left = `${Math.max(8, Math.min(x, innerWidth - w - 8))}px`;
    m.style.top = `${Math.max(8, Math.min(y, innerHeight - h - 8))}px`;
    find.focus({ preventScroll: true });
    setTimeout(() => addEventListener('pointerdown', away, true));
  }

  /** Pan and zoom to show every node. */
  private fit() {
    const ns = this.g.nodes, r = this.canvas.getBoundingClientRect();
    if (!ns.length || !r.width) { this.view = { x: 40, y: 30, k: 1 }; return; }
    const x0 = Math.min(...ns.map(n => n.x)), y0 = Math.min(...ns.map(n => n.y)), x1 = Math.max(...ns.map(n => n.x)) + 220, y1 = Math.max(...ns.map(n => n.y)) + 140;
    const k = Math.min(1, Math.max(0.25, Math.min((r.width - 40) / (x1 - x0), (r.height - 40) / (y1 - y0))));
    this.view = { k, x: 20 - x0 * k, y: 20 - y0 * k };
  }

  // ── saving, running ──
  private async save() {
    const name = this.m.name.trim();
    if (!name) { this.say('name it first', true); this.bar.name.focus(); return; }
    if (!this.g.nodes.length) { this.say('add a node first', true); return; }
    try { compile(parseGraph(this.g)); } catch (e) { this.say(`saved, but it can't run yet: ${(e as Error).message}`, true); }
    try {
      await this.host.save({ ...this.m, name, steps: [] });
      this.m.name = name;
      if (!this.info.classList.contains('warn')) this.say(`saved ${name}`);
    } catch (e) { this.say(`save: ${(e as Error).message}`, true); }
    this.drawBar();
  }

  private async drop() {
    const name = this.m.name.trim();
    if (!this.saved()?.graph || !confirm(`Delete the node graph ${name}? (from this scene)`)) return;
    try { await this.host.drop(name); this.say(`deleted ${name}: its nodes are still here until you pick another`); } catch (e) { this.say(`delete: ${(e as Error).message}`, true); }
    this.drawBar();
  }

  private async run() {
    this.marks.clear();
    this.say('');
    let g: Graph;
    try { g = parseGraph(this.g); compile(g); } catch (e) { this.say(`can't run it: ${(e as Error).message}`, true); this.markBad(e as Error); this.draw(); return; }
    this.draw();
    const tap: Tap = (r, out, why) => {
      const id = r.node;
      if (!id) return;
      if (this.running !== id) { this.els.get(this.running ?? '')?.classList.remove('run'); this.running = id; this.els.get(id)?.classList.add('run'); }
      const mk = this.marks.get(id) ?? {};
      if (why !== undefined) { mk.bad = true; mk.say = why; }
      else if (out) {
        if (out.say) mk.say = out.say;
        if (r.s.t === 'keep' && out.sel.length) { let n = 0; for (const m of out.sel) for (let i = 0; i < m.length; i++) n += m[i]; mk.n = n; }
      }
      this.marks.set(id, mk);
    };
    const r = await this.host.run({ ...this.m, name: this.m.name.trim(), steps: [], graph: g }, tap);
    this.running = null;
    this.draw();
    this.say(r.ok ? 'ran: each node shows the points it selected (hover a title for what it said)' : r.say, !r.ok);
  }

  /** A node an error names ("… (n3): …"): marked. */
  private markBad(e: Error) {
    const id = /\((n\d+)\)/.exec(e.message)?.[1];
    if (id) this.marks.set(id, { bad: true, say: e.message });
  }
}

/** Throws when wiring into `to` makes a loop (compile names it). */
function compileLoops(g: Graph, to: string) {
  const seen = new Set<string>(), stack = [to];
  const down = (id: string) => g.wires.filter(w => w.from === id).map(w => w.to);
  // a loop: from `to` its outputs lead back to it
  while (stack.length) {
    const id = stack.pop()!;
    for (const nx of down(id)) {
      if (nx === to) throw new Error('that wire would make a loop: a node\'s output coming back into it');
      if (!seen.has(nx)) { seen.add(nx); stack.push(nx); }
    }
  }
}
